import {
  type EventRegistrationConfig,
  MAX_REGISTRATION_QUESTIONS,
  type RegistrationAnswerValue,
  type RegistrationQuestion,
  type RegistrationQuestionType,
} from '@pulsify/shared/dto'

export interface QuestionDraft {
  id: string
  type: RegistrationQuestionType
  label: string
  required: boolean
  /** Choice options, one per line. */
  options: string
}

export interface RegistrationDraft {
  enabled: boolean
  questions: QuestionDraft[]
  seatLimit: string
  waitlist: boolean
  /** `YYYY-MM-DDTHH:mm` in local time, as a datetime-local input holds it; empty for none. */
  deadline: string
}

const CHOICE_TYPES: ReadonlySet<RegistrationQuestionType> = new Set([
  'single_choice',
  'multiple_choice',
])

export function isChoiceType(type: RegistrationQuestionType): boolean {
  return CHOICE_TYPES.has(type)
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function draftFromConfig(config: EventRegistrationConfig): RegistrationDraft {
  return {
    enabled: config.enabled,
    questions: config.questions.map((question) => ({
      id: question.id,
      type: question.type,
      label: question.label,
      required: question.required,
      options: (question.options ?? []).join('\n'),
    })),
    seatLimit: config.seatLimit === null ? '' : String(config.seatLimit),
    waitlist: config.waitlist,
    deadline: toLocalInput(config.deadline),
  }
}

export function configFromDraft(draft: RegistrationDraft): EventRegistrationConfig {
  const seats = Number.parseInt(draft.seatLimit, 10)
  return {
    enabled: draft.enabled,
    questions: draft.questions.slice(0, MAX_REGISTRATION_QUESTIONS).map((question) => {
      const base: RegistrationQuestion = {
        id: question.id,
        type: question.type,
        label: question.label.trim(),
        required: question.required,
      }
      if (!isChoiceType(question.type)) return base
      const options = [
        ...new Set(
          question.options
            .split('\n')
            .map((option) => option.trim())
            .filter(Boolean),
        ),
      ]
      return { ...base, options }
    }),
    seatLimit: Number.isFinite(seats) && seats > 0 ? seats : null,
    waitlist: draft.waitlist,
    deadline: draft.deadline ? new Date(draft.deadline).toISOString() : null,
  }
}

export function newQuestion(): QuestionDraft {
  return {
    id: `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    type: 'short_text',
    label: '',
    required: false,
    options: '',
  }
}

export function moveQuestion(
  questions: QuestionDraft[],
  index: number,
  delta: -1 | 1,
): QuestionDraft[] {
  const target = index + delta
  if (target < 0 || target >= questions.length) return questions
  const next = [...questions]
  const [moved] = next.splice(index, 1)
  if (moved) next.splice(target, 0, moved)
  return next
}

/** One answer as text for the list: choices joined, a checkbox as yes or no. */
export function answerLabel(
  value: RegistrationAnswerValue | undefined,
  yes: string,
  no: string,
): string {
  if (value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? yes : no
  return String(value)
}
